import styles from "./FamiliesList.module.scss";
import { ItemsList } from "../../../../common/components/itemsList/ItemsList";
import { Icon, IconType } from "../../../../common/icons/Icon";
import { useAddAdditionalLayoutButton } from "../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import {
  constructFamilyHref,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import { useNavigate } from "react-router-dom";
import { classNames } from "primereact/utils";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { OnboardingItemKey } from "../../../../common/enums";
import { usePageGuide } from "../../../onboarding/hooks/usePageGuide";

export const FamiliesList = () => {
  useAddAdditionalLayoutButton({
    iconType: IconType.PlusIcon,
    href: routePaths.familyAdd,
  });

  usePageGuide(OnboardingItemKey.FamiliesGuide);

  const { data: families, isLoading } = useApiQuery<
    IFamiliesAdministrationEmployeeModel[]
  >({
    queryKey: [endpointsMetadata.family.getAll],
    endpoint: endpointsMetadata.family.getAll,
    globalIsLoading: false,
  });

  return (
    <ItemsList
      isLoading={isLoading}
      className={styles.root}
      title="Налични семейства"
      noItemsText="Няма намерени семейства."
      itemTemplate={(item, index, actionButtons, className) => {
        return (
          <FamilyItem
            className={className}
            family={item}
            key={item?.id ?? index}
            isLoading={isLoading}
            actionButtons={actionButtons}
          />
        );
      }}
      items={families ?? []}
      swipeLeftActions={[
        {
          method: "delete",
          enablePrompt: true,
          promptMessage:
            "Сигурни ли сте, че искате да изтриете това семейство?",
          iconType: IconType.DeleteIcon,
          endpoint: endpointsMetadata.family.deleteFamily,
          validator: (item: IFamiliesAdministrationEmployeeModel) =>
            !item.isDeleted,
          invalidateKeys: [[endpointsMetadata.family.getAll]],
        },
        {
          method: "put",
          enablePrompt: true,
          promptMessage:
            "Сигурни ли сте, че искате да възстановите това семейство?",
          iconType: IconType.PutBackIcon,
          endpoint: endpointsMetadata.family.putFamilyBack,
          validator: (item: IFamiliesAdministrationEmployeeModel) =>
            item.isDeleted,
          invalidateKeys: [[endpointsMetadata.family.getAll]],
        },
      ]}
      swipeRightActions={[
        {
          type: "edit",
          iconType: IconType.EditIcon,
          endpoint: routePaths.familyEdit,
          validator: (item: IFamiliesAdministrationEmployeeModel) =>
            !item.isDeleted,
        },
      ]}
      deletedFilter={{
        storageKey: "families",
        isDeleted: (item: IFamiliesAdministrationEmployeeModel) =>
          !!item.isDeleted,
        sectionTitle: "Изтрити семейства",
        toggleLabel: "Покажи изтритите семейства",
      }}
      showItemsCount
      enableSearch
      fullHeight
    />
  );
};

interface IEmployeesAdministrationEmployeeProps {
  className?: string;
  family?: IFamiliesAdministrationEmployeeModel;
  isLoading: boolean;
  actionButtons?: React.ReactNode;
}

const FamilyItem = ({
  className,
  family,
  isLoading,
  actionButtons,
}: IEmployeesAdministrationEmployeeProps) => {
  const navigate = useNavigate();

  const onClick = () => {
    if (family) {
      const href = constructFamilyHref(family.id);
      navigate(href);
    }
  };

  return (
    <div
      className={classNames(className, styles.item, {
        [styles.item_resigned]: family?.isDeleted,
      })}
      onClick={onClick}
    >
      <div className={styles.item_left}>
        <SkeletonItem
          isLoading={isLoading}
          value={
            <div className={styles.item_left_name}>
              {family?.name}
              <span className={styles.item_left_name_id}>
                (ID: {family?.id}
                {family?.originalStudio && `, ${family.originalStudio}`})
              </span>
            </div>
          }
        />
      </div>
      {actionButtons}
      <Icon type={IconType.RightChevronIcon} />
    </div>
  );
};
